import { IconButton } from "@hortis/ui/button";
import { AlertCircle, XClose } from "@hortis/ui/icons";
import CircularProgress from "@mui/material/CircularProgress";
import { twMerge } from "tailwind-merge";
import type { StagedImage } from "./add-material-photos-button";
import { PhotoDialog } from "./photo-dialog";

export const MaterialPhotos = ({
  images,
  deletePhoto,
  onPhotoSave,
}: {
  images: Array<StagedImage>;
  deletePhoto: (id: string) => void;
  onPhotoSave: (id: string, image: StagedImage) => void;
}) => {
  return images.length === 0 ? (
    <></>
  ) : (
    <div className="flex flex-wrap gap-1.5 justify-self-start rounded-lg border border-grey-300 bg-white p-1 shadow-xs">
      {images.map((image) => (
        <div key={image.id} className="relative flex">
          <PhotoDialog
            initialValue={image}
            onSubmit={(data) => {
              onPhotoSave(image.id, { ...image, ...data });
            }}
          >
            <button
              className={twMerge(
                "relative h-12 w-16 overflow-hidden rounded-md border border-transparent ring-grey-400/60 transition duration-300 animate-in fade-in",
                image.complete && "hover:ring-2 hover:brightness-110",
              )}
              type="button"
              disabled={!image.complete}
              data-cy="material-photo-thumbnail"
            >
              <img
                src={image.thumb}
                alt="Uploaded"
                className="h-full w-full object-cover"
              />
              <div
                className={twMerge(
                  "absolute left-0 top-0 flex h-full w-full items-center justify-center transition duration-300",
                  image.complete ? "opacity-0" : "bg-white/30",
                  image.error != null && "bg-error-300/70",
                )}
              >
                {image.error == null ? (
                  <>
                    <CircularProgress
                      size={24}
                      variant="determinate"
                      value={image.progress}
                      thickness={7}
                      sx={{
                        color: "rgba(0,0,0,0.6)",
                      }}
                    />
                    <CircularProgress
                      size={24}
                      variant="determinate"
                      value={100}
                      data-cy="image-progress-indicator"
                      className="absolute"
                      thickness={7}
                      sx={{
                        color: "rgba(0,0,0,0.3)",
                      }}
                    />
                  </>
                ) : (
                  <div
                    className="flex items-center justify-center rounded-full bg-error-200/70 p-0.5"
                    data-cy="image-upload-error"
                  >
                    <AlertCircle className="h-5 w-5 text-error-800" />
                  </div>
                )}
              </div>
            </button>
          </PhotoDialog>
          {image.error != null && (
            <IconButton
              ariaLabel="Delete photo"
              onClick={() => {
                deletePhoto(image.id);
              }}
              className="absolute -right-1.5 -top-1.5 rounded-full p-0.5 animate-in fade-in"
              size="xs"
              data-cy="delete-photo"
            >
              <XClose className="h-3 w-3" />
            </IconButton>
          )}
        </div>
      ))}
    </div>
  );
};
